// 9.12 Approve a save offer (docs/SYSTEM_MAP.md, Connect › Decide). The deal agent made one offer after the first "no",
// inside the business's limits; a person approves it (in their own words if they like) or rejects it, which closes the
// deal as lost and hands the firm to ReConnect. Every write goes through act (decide_save_offer).
import { useId, useState } from "react";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { offerText } from "../../lib/connect";
import { Async, Empty, KeyValues, StatusChip } from "../../components/ui";
import { EntityPlaybook } from "../../components/FlavourChip";
import { Decide } from "./TaskCards";

export function SaveOfferTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const offerId = String(task.payload.offer_id ?? "");
  const dealId = String(task.payload.deal_id ?? task.entity_id ?? "");
  const offer = useAsync(async () => (await crm.connectOffers({ deal: dealId })).find((o) => o.id === offerId) ?? null, [crm, offerId, dealId]);
  const [body, setBody] = useState<string | null>(null);
  const id = useId();
  const max = typeof task.payload.max_discount_pct === "number" ? task.payload.max_discount_pct : null;
  return (
    <Async state={offer} empty={(o) => (!o ? <Empty title="This offer no longer exists" /> : null)}>
      {(o) => {
        const text = body ?? o!.body ?? "";
        return (
          <div>
            <p className="card-lede">
              <strong>{String(task.payload.organisation ?? "This firm")}</strong> said no
              {o!.reason ? (
                <>
                  : “<span>{o!.reason}</span>”
                </>
              ) : null}
              . Before the deal closes, the engine suggests one save offer.
            </p>
            <EntityPlaybook crm={crm} entityType="deal" entityId={dealId} />
            <KeyValues
              rows={[
                ["Offer", offerText(o!)],
                ["Your limit", max !== null ? `Up to ${max}% off` : "—"],
                ["Status", <StatusChip key="s" status={o!.status} />],
              ]}
            />
            {o!.status === "awaiting_approval" ? (
              <>
                <label className="field" htmlFor={id}>
                  <span className="field-label">The email (change it if you like)</span>
                  <textarea id={id} rows={7} value={text} onChange={(e) => setBody(e.target.value)} />
                </label>
                <p className="muted small">Approve and it goes as written, signed off by you. Reject and the deal closes as lost; the firm goes to ReConnect with its reason. One save offer per deal.</p>
                <Decide
                  approveLabel="Approve and send"
                  approveDisabled={!text.trim()}
                  onApprove={async () => {
                    await crm.decideSaveOffer(o!.id, true, body !== null && body.trim() !== (o!.body ?? "").trim() ? { body } : {});
                    onDone();
                  }}
                  onReject={async (note) => {
                    await crm.decideSaveOffer(o!.id, false, { note });
                    onDone();
                  }}
                />
              </>
            ) : (
              <p className="muted">This offer has already been decided.</p>
            )}
          </div>
        );
      }}
    </Async>
  );
}
